/**
 * Minimal glob allowlist matcher for absolute filesystem paths.
 *
 * No glob library is a dependency of this workspace. The patterns admins and
 * project owners write here are simple (`/tmp/claude/plans/*`, maybe `**`
 * for subdirectories) and this is a security boundary, so a small, fully
 * audited matcher beats pulling in a general-purpose glob engine whose edge
 * cases (brace expansion, extglob, negation) this feature never needs.
 *
 * `*` matches within a single path segment (never crosses `/`); `**`
 * matches across segments, including zero of them. Every other character is
 * escaped literally before being compiled to a regex, and the match is
 * anchored (`^...$`) so a pattern can never match a mere prefix of a path.
 *
 * Lives in `shared` rather than in the server because the web client decides
 * whether a file path mentioned in a chat message is even worth offering as
 * a link, and it has to reach the same verdict the server will — a second
 * implementation would drift and either dangle links that error on click or
 * hide paths that are in fact previewable. The server still re-checks every
 * request: this is the same rule, not a substitute for enforcing it.
 */

const REGEX_SPECIAL_CHARS = /[.+^${}()|[\]\\]/g;

function escapeRegExpLiteral(segment: string): string {
  return segment.replace(REGEX_SPECIAL_CHARS, "\\$&");
}

/**
 * Compiles one glob pattern to an anchored regex.
 *
 * Splits on the wildcard runs first (`**` before `*`, since `*` is a prefix
 * of `**`) so each literal chunk in between is escaped independently — this
 * is what keeps a pattern like `/tmp/claude/plans/*.md` from having its `.`
 * interpreted as "any character" once translated to regex.
 */
export function compileGlob(pattern: string): RegExp {
  let source = "";
  let rest = pattern;
  while (rest.length > 0) {
    const nextWildcard = rest.search(/\*\*|\*/);
    if (nextWildcard < 0) {
      source += escapeRegExpLiteral(rest);
      break;
    }
    source += escapeRegExpLiteral(rest.slice(0, nextWildcard));
    if (rest.startsWith("**", nextWildcard)) {
      source += ".*";
      rest = rest.slice(nextWildcard + 2);
    } else {
      source += "[^/]*";
      rest = rest.slice(nextWildcard + 1);
    }
  }
  return new RegExp(`^${source}$`);
}

/**
 * True when `absolutePath` matches at least one of `patterns`.
 *
 * Compiles patterns on every call rather than caching: allowlists are short
 * (tens of entries at most) and a cache would need invalidation whenever the
 * admin setting or a project's `.taskin/config.json` changes — not worth it
 * for a handful of regex compiles.
 */
export function matchesAnyGlob(absolutePath: string, patterns: readonly string[]): boolean {
  return patterns.some((pattern) => compileGlob(pattern).test(absolutePath));
}

/**
 * Whether a path mentioned in free text is worth offering as a preview link.
 *
 * Only absolute paths qualify: the server refuses anything else outright, so
 * a relative-looking token can never resolve to a preview no matter which
 * patterns are configured. With no patterns configured this is always false
 * — an unconfigured install offers no links at all.
 */
export function isPreviewablePath(candidate: string, patterns: readonly string[]): boolean {
  if (!candidate.startsWith("/")) return false;
  return matchesAnyGlob(candidate, patterns);
}
